// The client side of /api/transcripts (BUILD-BRIEF-R2 §7, §14) — owned by the `triage` agent.
//
// Two readings live behind this one path because one service owns both (services/triage.ts):
// an uploaded meeting transcript, and the "Draft reply" verb on a stored message. Both answer
// with a `Run` as soon as it is QUEUED — not when it finishes. The run's proposals appear in
// Review minutes later under the principal `run:<id>`; nothing arrives on the record by itself.
import { request, upload } from './client.js';
import type { InteractionDetail, ListResponse, Run } from '../../shared/types.js';

export interface TranscriptUploadFields {
  /** The people who were in the room, as attendees on the interaction. */
  person_ids?: number[];
  organisation_id?: number | null;
  /** Required, YYYY-MM-DD: the day the meeting was held, not the day of the upload. */
  occurred_on: string;
  title?: string;
}

/**
 * The interaction is IMPORTED (the operator's own material, their own click); what the run
 * reads out of it arrives as proposals. `run` is null — with `run_error` beside it — when the
 * transcript was stored but no run could start, which is an honest outcome, not a failure of
 * the upload.
 */
export interface TranscriptUploadResult {
  interaction: InteractionDetail;
  run: Run | null;
  run_error: string | null;
}

/** `.txt | .vtt | .md`, at most 5 MB. */
export function uploadTranscript(file: File, fields: TranscriptUploadFields): Promise<TranscriptUploadResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('occurred_on', fields.occurred_on);
  if (fields.title) form.append('title', fields.title);
  if (fields.organisation_id != null) form.append('organisation_id', String(fields.organisation_id));
  // One JSON array rather than repeated fields: the route reads all three shapes, and this is
  // the one that survives an empty list without meaning "no field at all".
  form.append('person_ids', JSON.stringify(fields.person_ids ?? []));
  return upload('/transcripts', form);
}

/** The runs that read one uploaded transcript, newest first. */
export function transcriptRuns(interactionId: number): Promise<ListResponse<Run>> {
  return request(`/transcripts/${interactionId}/runs`);
}

/** The Inbox's "Draft reply" verb: one `mail_triage` run over one stored message. It proposes
 *  the commitments and facts in the mail and, when a reply is due, a draft for the outbox —
 *  which only the operator's own click ever sends. */
export function triageMessage(messageId: number): Promise<Run> {
  return request(`/transcripts/triage/${messageId}`, { method: 'POST' });
}

/** The runs that read one stored message, newest first. */
export function messageRuns(messageId: number): Promise<ListResponse<Run>> {
  return request(`/transcripts/triage/${messageId}/runs`);
}
